<template>
  <div class="landing-wrap">

    <!-- ═══ NAVBAR ═══ -->
    <header class="navbar-landing">
      <nav class="nav-inner">
        <a href="#" class="nav-logo" @click.prevent>
          <img src="@/assets/imagenes/logotipo.png" alt="Domédica" />
        </a>
        <div class="nav-actions">
          <div class="lang-switch">
            <button :class="{ active: locale === 'es' }" @click="setLang('es')">ES</button>
            <span class="lang-sep">|</span>
            <button :class="{ active: locale === 'en' }" @click="setLang('en')">EN</button>
          </div>
          <button class="btn-iniciar" @click="irLogin">{{ $t('nav.login') }}</button>
        </div>
      </nav>
    </header>

    <!-- ═══ HERO ═══ -->
    <section class="hero-section reveal reveal-up">
      <div class="hero-inner">
        <div class="hero-text reveal reveal-left">
          <div class="hero-badge">
            <span class="badge-dot"></span>
            {{ $t('hero.badge') }}
          </div>
          <h1>{{ $t('hero.h1') }}</h1>
          <i18n-t keypath="hero.sub" tag="p" class="hero-sub">
            <template #h><strong>24/7</strong></template>
          </i18n-t>
          <div class="hero-ctas">
            <!-- Botón "Agenda tu cita" eliminado -->
            <a href="#buscador" class="btn-cta-secondary" @click.prevent="scrollTo('buscador')">{{ $t('hero.cta_secondary') }}</a>
          </div>
          <div class="hero-pills">
            <span><span class="pill-dot"></span>{{ $t('hero.pill1') }}</span>
            <span><span class="pill-dot"></span>{{ $t('hero.pill2') }}</span>
            <span><span class="pill-dot"></span>{{ $t('hero.pill3') }}</span>
          </div>
        </div>
        <div class="hero-img-wrap reveal reveal-right">
          <img src="@/assets/imagenes/3D-Character-Medical-Animation.jpg" alt="Médico Domédica" />
        </div>
      </div>
    </section>

    <!-- ═══ CÓMO FUNCIONA ═══ -->
    <section id="agenda" class="steps-section reveal reveal-up">
      <div class="section-inner">
        <div class="section-header">
          <span class="section-tag">{{ $t('steps.tag') }}</span>
          <h2>{{ $t('steps.h2') }}</h2>
        </div>
        <div class="steps-grid">
          <div v-for="(step, i) in stepsIcons" :key="i" class="step-card reveal reveal-up">
            <div class="step-top">
              <span class="step-num">{{ i + 1 }}</span>
              <span class="step-icon material-icons">{{ step.icon }}</span>
            </div>
            <h3>{{ $t(`steps.items[${i}].title`) }}</h3>
            <p>{{ $t(`steps.items[${i}].desc`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ BUSCADOR DE EXPEDIENTES ═══ -->
    <section id="buscador" class="buscador-section reveal reveal-up">
      <div class="section-inner text-center">
        <span class="section-tag light">{{ $t('search.tag') }}</span>
        <h2 class="light">{{ $t('search.h2') }}</h2>
        <p class="buscador-sub">{{ $t('search.sub') }}</p>
        <div class="buscador-box">
          <input
            v-model="folio"
            type="text"
            :placeholder="$t('search.placeholder')"
            @keyup.enter="buscarFolio"
          />
          <button
            class="btn-buscador-icon"
            @click="buscarFolio"
            :disabled="buscando"
            :aria-label="buscando ? $t('search.searching') : $t('search.btn')"
            :title="buscando ? $t('search.searching') : $t('search.btn')"
          >
            <span v-if="!buscando" class="material-icons">search</span>
            <span v-else class="buscador-spinner"></span>
          </button>
        </div>
      </div>
    </section>

    <!-- ═══ FAQ ═══ -->
    <section class="faq-section reveal reveal-up">
      <div class="section-inner">
        <div class="section-header text-center">
          <span class="section-tag">{{ $t('faq.tag') }}</span>
          <h2>{{ $t('faq.h2') }}</h2>
        </div>
        <div class="faq-list">
          <div v-for="i in faqIndices" :key="i" class="faq-item reveal reveal-up">
            <button class="faq-btn" @click="toggleFaq(i)">
              <span>{{ $t(`faq.items[${i}].q`) }}</span>
              <span class="material-icons faq-icon" :class="{ open: openFaq === i }">add</span>
            </button>
            <transition name="faq-answer" mode="out-in">
              <div v-if="openFaq === i" class="faq-answer">
                <p>{{ $t(`faq.items[${i}].a`) }}</p>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ FOOTER ═══ -->
    <footer class="footer-landing reveal reveal-up">
      <div class="footer-inner">
        <div class="footer-brand">
          <img src="@/assets/imagenes/logotipo.png" alt="Domédica" />
          <p>{{ $t('footer.tagline') }}</p>
        </div>
        <div class="footer-cols">
          <div class="footer-col">
            <div class="footer-col-title">{{ $t('footer.contact') }}</div>
            <a href="mailto:domedicamx@gmail.com">domedicamx@gmail.com</a>
            <span class="footer-muted">{{ $t('footer.available') }}</span>
          </div>
          <div class="footer-col">
            <div class="footer-col-title">{{ $t('footer.account') }}</div>
            <a href="#" @click.prevent="irLogin">{{ $t('footer.login') }}</a>
            <a href="#buscador" @click.prevent="scrollTo('buscador')">{{ $t('footer.receipt') }}</a>
            <a href="#" @click.prevent="irEncuesta">{{ $t('footer.survey') }}</a>
            <a href="#" @click.prevent="mostrarServicios = true">{{ $t('services.tag') }}</a>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <span>{{ $t('footer.rights') }}</span>
        <div class="footer-legal-links">
          <router-link to="/privacidad">{{ $t('footer.privacy') }}</router-link>
          <router-link to="/terminos">{{ $t('footer.terms') }}</router-link>
        </div>
      </div>
    </footer>

    <!-- ═══ MODAL: SERVICIOS ADICIONALES ═══ -->
    <transition name="fade">
      <div v-if="mostrarServicios" class="modal-servicios-overlay" @click.self="mostrarServicios = false">
        <div class="modal-servicios">
          <button class="modal-servicios-cerrar" @click="mostrarServicios = false" aria-label="Cerrar">
            <span class="material-icons">close</span>
          </button>
          <span class="section-tag">{{ $t('services.tag') }}</span>
          <h2>{{ $t('services.h2') }}</h2>
          <p class="services-sub">{{ $t('services.sub') }}</p>
          <div class="services-table-wrap">
            <table class="services-table">
              <thead>
                <tr>
                  <th>{{ $t('services.concept') }}</th>
                  <th>{{ $t('services.base_price') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="i in serviceIndices" :key="i">
                  <td>{{ $t(`services.items[${i}].concept`) }}</td>
                  <td>{{ $t(`services.items[${i}].price`) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import { folioExiste } from '@/utils/api';
import i18n from '@/i18n';

export default {
  name: 'InicioPagina',
  data() {
    return {
      folio: '',
      buscando: false,
      openFaq: null,
      revealObserver: null,
      locale: localStorage.getItem('sanatia_lang') || 'es',
      stepsIcons: [
        { icon: 'room_service' },
        { icon: 'fact_check' },
        { icon: 'medical_services' },
        { icon: 'description' },
      ],
      faqIndices: [0, 1, 2],
      serviceIndices: [0, 1, 2, 3, 4, 5, 6, 7],
      mostrarServicios: false,
    };
  },
  mounted() {
    this.initRevealObserver();
  },

  beforeUnmount() {
    if (this.revealObserver) {
      this.revealObserver.disconnect();
    }
  },

  methods: {
    initRevealObserver() {
      const elements = this.$el.querySelectorAll('.reveal');

      if (!elements.length) return;

      if (!('IntersectionObserver' in window)) {
        elements.forEach(el => el.classList.add('visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            this.revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });

      elements.forEach((el) => this.revealObserver.observe(el));
    },

    setLang(lang) {
      this.locale = lang;
      localStorage.setItem('sanatia_lang', lang);
      i18n.global.locale.value = lang;
    },

    irLogin() {
      this.$router.push('/login');
    },

    irEncuesta() {
      this.$router.push('/encuesta');
    },

    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },

    toggleFaq(i) {
      this.openFaq = this.openFaq === i ? null : i;
    },

    async buscarFolio() {
      let folioLimpio = DOMPurify.sanitize(this.folio.trim())
        .split('')
        .filter(c => c.charCodeAt(0) > 0x1F && c.charCodeAt(0) !== 0x7F)
        .join('');

      if (!folioLimpio || folioLimpio.length > 50) {
        Swal.fire({
          icon: 'error',
          title: 'Folio inválido',
          text: 'Ingresa un folio válido.',
          confirmButtonColor: '#d33',
        });
        return;
      }

      this.buscando = true;
      try {
        if (await folioExiste(folioLimpio)) {
          this.$router.push(`/info/${folioLimpio}`);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Folio no encontrado',
            text: `El folio ${folioLimpio} no está registrado en el sistema.`,
            confirmButtonColor: '#d33',
          });
        }
      } catch {
        Swal.fire({
          icon: 'error',
          title: 'Error en la búsqueda',
          text: 'Ocurrió un error al buscar el folio.',
          confirmButtonColor: '#d33',
        });
      } finally {
        this.buscando = false;
      }
    },
  },
};
</script>

<style scoped>
/* ============================================================
   DOMÉDICA — LANDING PAGE
   ============================================================ */

/* Reset local para que la landing sea página completa */
.landing-wrap {
  width: 100%;
  overflow-x: hidden;
  background: #ffffff;
  font-family: var(--font-body);
}

/* ──────────────────────────────────────────────
   NAVBAR
────────────────────────────────────────────── */
.navbar-landing {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #ece6df;
}

.nav-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 14px clamp(18px, 4vw, 40px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.nav-logo img {

  width: 20%;
  display: block;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.lang-switch {
  display: flex;
  align-items: center;
  gap: 4px;
}

.lang-switch button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 2px;
  color: #aaa;
  font-size: 13px;
  font-weight: 600;
  font-family: var(--font-body);
  transition: color 0.15s;
}

.lang-switch button.active {
  color: var(--brand-primary);
}

.lang-switch button:hover {
  color: var(--brand-primary);
}

.lang-sep {
  color: #ccc;
  font-size: 12px;
  user-select: none;
}

.btn-iniciar {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 14px;
  color: var(--brand-primary);
  background: transparent;
  border: 1.5px solid var(--brand-primary);
  border-radius: 999px;
  padding: 9px 22px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
}

.btn-iniciar:hover {
  background: var(--brand-primary);
  color: #ffffff;
}

/* ──────────────────────────────────────────────
   HERO
────────────────────────────────────────────── */
.hero-section {
  background: var(--brand-beige);
}

.hero-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(40px, 7vw, 84px) clamp(18px, 4vw, 40px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(32px, 5vw, 64px);
}

.hero-text {
  flex: 1 1 360px;
  min-width: 280px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e6ddd2;
  border-radius: 999px;
  padding: 7px 15px;
  margin-bottom: 22px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: var(--brand-secondary);
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--brand-accent-sage);
  box-shadow: 0 0 0 3px rgba(96, 127, 112, 0.18);
  flex-shrink: 0;
}

.hero-text h1 {
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--brand-primary);
  font-size: clamp(30px, 5vw, 54px);
  line-height: 1.1;
  letter-spacing: -0.5px;
  margin-bottom: 20px;
}

.hero-sub {
  font-size: clamp(15px, 1.8vw, 18px);
  line-height: 1.65;
  color: var(--brand-secondary);
  max-width: 520px;
  margin-bottom: 30px;
}

.hero-sub strong {
  color: var(--brand-primary);
  font-weight: 700;
}

.hero-ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 30px;
}

.btn-cta-primary {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 15px;
  color: #ffffff;
  background: var(--brand-primary);
  text-decoration: none;
  border-radius: 999px;
  padding: 14px 28px;
  box-shadow: 0 10px 24px -10px rgba(19, 62, 104, 0.55);
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn-cta-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px -10px rgba(19, 62, 104, 0.65);
  color: #ffffff;
}

.btn-cta-secondary {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 15px;
  color: #ffffff;
  background: var(--brand-primary);
  text-decoration: none;
  border-radius: 999px;
  padding: 14px 28px;
  box-shadow: 0 10px 24px -10px rgba(19, 62, 104, 0.55);
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn-cta-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px -10px rgba(19, 62, 104, 0.65);
  color: #ffffff;
}

.hero-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.hero-pills span {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-secondary);
}

.pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--brand-accent-sage);
  flex-shrink: 0;
}

.hero-img-wrap {
  flex: 1 1 320px;
  min-width: 260px;
}

.hero-img-wrap img {
  width: 100%;
  height: auto;
  border-radius: 24px;
  box-shadow: 0 30px 60px -28px rgba(19, 62, 104, 0.4);
  display: block;
  object-fit: cover;
  aspect-ratio: 4 / 5;
}

/* ──────────────────────────────────────────────
   SECCIONES — helpers comunes
────────────────────────────────────────────── */
.section-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(52px, 7vw, 88px) clamp(18px, 4vw, 40px);
}

.section-inner.text-center {
  text-align: center;
}

.section-header {
  margin-bottom: clamp(36px, 4vw, 52px);
}

.section-header.text-center {
  text-align: center;
}

.section-tag {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--brand-accent-sage);
  display: block;
  margin-bottom: 12px;
}

.section-tag.light { color: #9db4cb; }

.section-header h2,
h2.light {
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--brand-primary);
  font-size: clamp(26px, 3.8vw, 40px);
  line-height: 1.14;
  letter-spacing: -0.3px;
  margin: 0;
}

h2.light { color: #ffffff; }

/* ──────────────────────────────────────────────
   ANIMACIONES
────────────────────────────────────────────── */
.reveal {
  opacity: 0;
  transform: translateY(28px) scale(0.985);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.2, 0.8, 0.2, 1);
  will-change: opacity, transform;
}

.reveal.visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.reveal-left {
  transform: translateX(-24px) scale(0.985);
}

.reveal-right {
  transform: translateX(24px) scale(0.985);
}

.reveal.visible.reveal-left,
.reveal.visible.reveal-right {
  transform: translateX(0) scale(1);
}

.faq-answer-enter-active,
.faq-answer-leave-active {
  overflow: hidden;
  transition: opacity 0.25s ease, transform 0.25s ease, max-height 0.25s ease;
}

.faq-answer-enter-from,
.faq-answer-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-8px);
}

.faq-answer-enter-to,
.faq-answer-leave-from {
  opacity: 1;
  max-height: 500px;
  transform: translateY(0);
}

/* ──────────────────────────────────────────────
   PASOS
────────────────────────────────────────────── */
.steps-section {
  background: #ffffff;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(14px, 2vw, 22px);
}

.step-card {
  background: #ffffff;
  border: 1px solid #ece6df;
  border-radius: 20px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
}

.step-card:hover {
  box-shadow: 0 22px 44px -26px rgba(19, 62, 104, 0.35);
  transform: translateY(-4px);
  border-color: #dccfbd;
}

.step-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-num {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 36px;
  color: var(--brand-primary);
  opacity: 0.14;
  line-height: 1;
}

.step-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--brand-beige);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-primary);
  font-size: 20px;
}

.step-card h3 {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 18px;
  color: var(--brand-primary);
  line-height: 1.2;
  margin: 0;
}

.step-card p {
  font-size: 14px;
  line-height: 1.6;
  color: var(--brand-secondary);
  margin: 0;
}

/* ──────────────────────────────────────────────
   BUSCADOR
────────────────────────────────────────────── */
.buscador-section {
  background: var(--brand-primary);
  position: relative;
}

.buscador-sub {
  font-family: var(--font-body);
  font-size: clamp(14px, 1.7vw, 16px);
  line-height: 1.6;
  color: #c5d3e1;
  max-width: 540px;
  margin: 14px auto 28px;
}

.buscador-box {
  background: #ffffff;
  border-radius: 16px;
  padding: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  max-width: 580px;
  margin: 0 auto;
  box-shadow: 0 24px 50px -24px rgba(0, 0, 0, 0.45);
}

.buscador-box input {
  flex: 1 1 200px;
  min-width: 160px;
  border: none;
  outline: none;
  font-size: 16px;
  color: var(--brand-primary);
  padding: 13px 16px;
  background: transparent;
  font-weight: 500;
  font-family: var(--font-body);
}

.buscador-box input::placeholder {
  color: var(--brand-secondary-light);
  font-weight: 400;
}

.buscador-box button.btn-buscador-icon {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--brand-primary);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}

.buscador-box button.btn-buscador-icon .material-icons {
  font-size: 22px;
}

.buscador-box button.btn-buscador-icon:hover:not(:disabled) {
  background: var(--brand-primary-dark);
  transform: scale(1.05);
}

.buscador-box button.btn-buscador-icon:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.buscador-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  display: inline-block;
  animation: buscador-spin 0.7s linear infinite;
}

@keyframes buscador-spin {
  to { transform: rotate(360deg); }
}

/* ──────────────────────────────────────────────
   FAQ
────────────────────────────────────────────── */
.faq-section {
  background: var(--brand-beige);
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 840px;
  margin: 0 auto;
}

.faq-item {
  background: #ffffff;
  border: 1px solid #ece6df;
  border-radius: 14px;
  overflow: hidden;
}

.faq-btn {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 22px;
  text-align: left;
}

.faq-btn span:first-child {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: clamp(14px, 1.8vw, 16px);
  color: var(--brand-primary);
  line-height: 1.3;
}

.faq-icon {
  font-size: 24px;
  transition: transform 0.3s ease;
  color: var(--brand-primary);
  line-height: 1;
}

.faq-icon.open {
  transform: rotate(45deg);
}

.faq-answer {
  padding: 0 22px 22px;
  animation: faqIn 0.22s ease;
}

.faq-answer p {
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--brand-secondary);
  border-top: 1px solid #f0eae2;
  padding-top: 16px;
  margin: 0;
}

@keyframes faqIn {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ──────────────────────────────────────────────
   SERVICIOS ADICIONALES (modal desde el footer)
────────────────────────────────────────────── */
.modal-servicios-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 16px;
}

.modal-servicios {
  position: relative;
  width: min(760px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 18px;
  padding: 40px clamp(20px, 4vw, 44px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.modal-servicios-cerrar {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: var(--brand-secondary-light);
  cursor: pointer;
  padding: 4px;
  display: flex;
}

.modal-servicios-cerrar:hover {
  color: var(--brand-primary);
}

.services-sub {
  max-width: 640px;
  margin: 10px auto 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--brand-secondary);
}

.services-table-wrap {
  max-width: 840px;
  margin: 24px auto 0;
  overflow-x: auto;
  text-align: left;
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
}

.services-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14.5px;
}

.services-table thead th {
  background: var(--brand-primary);
  color: #ffffff;
  text-align: left;
  padding: 14px 20px;
  font-family: var(--font-heading);
  font-weight: 600;
}

.services-table thead th:last-child {
  text-align: right;
}

.services-table tbody td {
  padding: 14px 20px;
  border-bottom: 1px solid #ece6df;
  color: var(--brand-secondary);
}

.services-table tbody td:last-child {
  text-align: right;
  font-weight: 700;
  color: var(--brand-primary);
  white-space: nowrap;
}

.services-table tbody tr:last-child td {
  border-bottom: none;
}

/* ──────────────────────────────────────────────
   FOOTER
────────────────────────────────────────────── */
.footer-landing {
  background: #ffffff;
  border-top: 1px solid #ece6df;
}

.footer-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(40px, 5vw, 56px) clamp(18px, 4vw, 40px) 28px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 32px;
  align-items: flex-start;
}

.footer-brand {
  max-width: 300px;
}

.footer-brand img {
  height: 100px;
  width: auto;
  margin-bottom: 14px;
  display: block;
}

.footer-brand p {
  font-size: 13px;
  line-height: 1.6;
  color: var(--brand-secondary-light);
  margin: 0;
}

.footer-cols {
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
}

.footer-col {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.footer-col-title {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--brand-primary);
  margin-bottom: 5px;
}

.footer-col a {
  font-size: 13.5px;
  color: var(--brand-secondary);
  text-decoration: none;
  transition: color 0.15s;
}

.footer-col a:hover { color: var(--brand-primary); }

.footer-muted {
  font-size: 13.5px;
  color: var(--brand-secondary-light);
}

.footer-bottom {
  max-width: 1180px;
  margin: 0 auto;
  padding: 20px clamp(18px, 4vw, 40px) 28px;
  border-top: 1px solid #ece6df;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.footer-bottom span {
  font-size: 12.5px;
  color: var(--brand-secondary-light);
}

.footer-bottom a {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-primary);
  text-decoration: none;
}

.footer-bottom a:hover { text-decoration: underline; }

.footer-legal-links {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

/* ──────────────────────────────────────────────
   RESPONSIVE
────────────────────────────────────────────── */
@media (max-width: 768px) {
  .hero-inner {
    flex-direction: column;
  }

  .hero-img-wrap {
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }

  .hero-img-wrap img {
    aspect-ratio: 16 / 10;
  }

  .hero-ctas {
    flex-direction: column;
  }

  .btn-cta-primary,
  .btn-cta-secondary {
    text-align: center;
  }

  .footer-inner {
    flex-direction: column;
  }

  .footer-brand { max-width: 100%; }

  .buscador-box input {
    width: 100%;
  }

  .buscador-box button.btn-buscador-icon {
    align-self: center;
  }

  .section-inner {
    padding-top: clamp(30px, 8vw, 52px);
    padding-bottom: clamp(30px, 8vw, 52px);
  }

  .section-header {
    margin-bottom: 24px;
  }

  .steps-grid {
    gap: 12px;
  }

  .step-card {
    padding: 18px 16px;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .nav-inner { padding: 12px 16px; }
  .btn-iniciar { font-size: 13px; padding: 8px 16px; }
  .steps-grid { grid-template-columns: 1fr; }
  .section-inner {
    padding-top: 22px;
    padding-bottom: 22px;
  }
  .step-card {
    padding: 14px 14px;
  }
  .hero-pills { gap: 12px; }
  .footer-cols { flex-direction: column; gap: 24px; }
}
</style>